import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { useUserRole } from '../hooks/useUserRole';
import { fetchWithOrg } from '../utils/api';
import ReactMarkdown from 'react-markdown';
import { useToast } from '../hooks/use-toast';
import { getOutputStyleProfile, detectAgentCategory } from '../utils/outputStyles';
import { exportToMarkdown, exportToPDF, exportToDOCX } from '../utils/exportTemplates';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import UniversalExportModal from '../components/UniversalExportModal';
import AIOutputNotice from '../components/AIOutputNotice';

// Universal Output Framework v2.0 - New standardized output viewer
import { UniversalOutputViewer } from '../components/output';

import {
  ArrowLeft,
  ArrowSquareOut,
  ArrowsClockwise,
  Bookmark,
  CaretDown,
  CaretUp,
  CheckCircle,
  CheckSquare,
  CircleNotch,
  Clock,
  Copy,
  Database,
  DownloadSimple,
  FileAudio,
  FileText,
  Lightbulb,
  Lightning,
  Link as LinkIcon,
  MagnifyingGlass,
  Microphone,
  PaperPlaneRight,
  Play,
  ShareNetwork,
  SpeakerHigh,
  Target,
  Tray,
  WarningCircle
} from '@phosphor-icons/react';
// Output-Type-Aware Renderers (legacy)
import { 
  InsightRenderer, 
  ActionRenderer, 
  DataRenderer, 
  DocumentRenderer, 
  PlainTextRenderer,
  detectRendererType,
  getRendererLabel,
  parseMarkdownToSections
} from '../components/output-renderers';

/**
 * Session Detail Page - Business AI Operating System
 * 
 * Core Principle: Agents do not choose layouts. Output structure determines rendering.
 * 
 * Renderer Selection Priority:
 * 1. output.sections[] → Insight Renderer
 * 2. output.action_payload[] → Action Renderer
 * 3. output.structured_json → Data Renderer
 * 4. output.markdown → Document Renderer
 * 5. Fallback → Plain Text Renderer
 */
const SessionDetail = () => {
  const { session_id } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { role, isAdmin } = useUserRole();
  const { toast } = useToast();
  const [session, setSession] = useState(null);
  const [outputData, setOutputData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outputLoading, setOutputLoading] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isReviewed, setIsReviewed] = useState(false);
  const [inboxItemExists, setInboxItemExists] = useState(false);
  const [sendingToInbox, setSendingToInbox] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Only show "Send to Inbox" for member users, not admins
  const showSendToInbox = role === 'member' || role === 'employee' || role === 'viewer';

  useEffect(() => {
    if (activeOrganization && session_id) {
      fetchSession();
    }
  }, [activeOrganization, session_id]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/sessions/${session_id}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setSession(data);
          
          // Fetch persisted output if available
          if (data.output_ref?.output_id || data.status === 'completed') {
            await fetchOutput();
          }
        }
      }
    } catch (err) {
      console.error('Error fetching session:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutput = async () => {
    try {
      setOutputLoading(true);
      console.log('[SessionDetail] Fetching output for session:', session_id);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/sessions/${session_id}/output`,
        {},
        activeOrganization.organization_id
      );

      console.log('[SessionDetail] Output response status:', response.status, response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[SessionDetail] Output data:', {
          success: data.success,
          source: data.source,
          hasOutput: !!data.output,
          format: data.output?.format,
          hasSections: data.output?.content?.sections?.length
        });
        if (data.success && data.output) {
          setOutputData(data.output);
          console.log('[SessionDetail] Set outputData successfully');
        } else {
          console.log('[SessionDetail] Output not set - success:', data.success, 'output:', !!data.output);
        }
      } else {
        console.log('[SessionDetail] Response not OK:', response.status);
      }
    } catch (err) {
      console.error('Error fetching output:', err);
    } finally {
      setOutputLoading(false);
    }
  };

  // Check if inbox item already exists for this session
  const checkInboxStatus = async () => {
    // Use session.id if available (the actual UUID), fallback to route param
    const effectiveSessionId = session?.id || session_id;
    
    if (!effectiveSessionId) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/inbox/check-session/${effectiveSessionId}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setInboxItemExists(data.exists);
      }
    } catch (err) {
      // Silently fail
    }
  };

  // Check inbox status when session loads
  useEffect(() => {
    if (session && activeOrganization) {
      checkInboxStatus();
    }
  }, [session, activeOrganization]);

  // PaperPlaneRight to Tray
  const handleSendToInbox = async () => {
    if (inboxItemExists || sendingToInbox) return;
    
    // Determine the correct session ID to use
    // Sessions use 'session_id' field as primary identifier (e.g., "session_abc123")
    // Priority: session.session_id > session.id > route param
    const effectiveSessionId = session?.session_id || session?.id || session_id;
    
    // Dev-safe logging (no secrets)
    if (process.env.NODE_ENV === 'development') {
      console.log('SendToInbox', { 
        effectiveSessionId, 
        fromSessionField: session?.session_id,
        fromIdField: session?.id, 
        fromRoute: session_id,
        sessionOrgId: session?.org_id
      });
    }
    
    // Block if no valid session ID
    if (!effectiveSessionId) {
      toast({
        title: 'Cannot Send to Inbox',
        description: 'Session ID is missing. Please refresh the page and try again.',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSendingToInbox(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/inbox/items/from-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: effectiveSessionId,
            title: null, // Let backend auto-generate
            summary: null,
            category: agentCategory,
            priority: 'medium'
          })
        },
        activeOrganization.organization_id
      );
      
      // Parse response body for structured errors
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error('Failed to parse response:', parseErr);
      }
      
      if (response.ok && data?.success) {
        setInboxItemExists(true);
        toast({
          title: 'Sent to Inbox',
          description: 'This outcome is now in your Inbox for review.',
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        return;
      }
      
      // Handle specific error cases
      const errorCode = data?.error;
      const errorMessage = data?.message || 'An error occurred';
      const suggestion = data?.suggestion;
      
      // Log error details in development
      console.error('Send to Inbox failed:', {
        status: response.status,
        endpoint: `${BACKEND_URL}/api/inbox/items/from-session`,
        error: errorCode,
        message: errorMessage,
        suggestion,
        session_id: effectiveSessionId,
        response_session_id: data?.session_id,
        response_org_id: data?.org_id
      });
      
      if (response.status === 409 || errorCode === 'duplicate') {
        // Duplicate - already in inbox
        setInboxItemExists(true);
        toast({
          title: 'Already in Inbox',
          description: suggestion || 'This session is already in your Inbox.',
          className: 'bg-[#1a1a1a] border-[rgba(255,255,255,0.2)] text-white'
        });
        return;
      }
      
      if (response.status === 403 || errorCode === 'forbidden') {
        toast({
          title: 'Access Denied',
          description: suggestion || errorMessage,
          variant: 'destructive'
        });
        return;
      }
      
      if (response.status === 404 || errorCode === 'not_found') {
        toast({
          title: 'Session Not Found',
          description: suggestion || errorMessage,
          variant: 'destructive'
        });
        return;
      }
      
      if (response.status === 400 || errorCode === 'missing_session_id') {
        toast({
          title: 'Invalid Request',
          description: suggestion || errorMessage,
          variant: 'destructive'
        });
        return;
      }
      
      // Generic error with message from backend
      toast({
        title: 'Failed to Send',
        description: suggestion ? `${errorMessage}. ${suggestion}` : errorMessage,
        variant: 'destructive'
      });
      
    } catch (err) {
      console.error('Error sending to inbox:', err);
      toast({
        title: 'Connection Error',
        description: 'Unable to reach the server. Please check your connection and try again.',
        variant: 'destructive'
      });
    } finally {
      setSendingToInbox(false);
    }
  };

  const handleRerun = async () => {
    try {
      setRerunning(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/sessions/${session_id}/run`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        await fetchSession();
        toast({
          title: 'Agent Restarted',
          description: 'The agent is now processing your request again.',
          className: 'bg-[#1a1a1a] border-[#6366F1]/30 text-white'
        });
      }
    } catch (err) {
      console.error('Error rerunning session:', err);
    } finally {
      setRerunning(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Copy formatted output to clipboard
  const handleCopyOutput = async () => {
    try {
      const content = outputData?.content;
      let textToCopy = '';
      
      if (content?.markdown) {
        textToCopy = content.markdown;
      } else if (content?.sections?.length > 0) {
        textToCopy = content.sections.map(s => `## ${s.label || s.key}\n\n${s.content}`).join('\n\n');
      } else if (content?.json_data) {
        textToCopy = JSON.stringify(content.json_data, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      toast({
        title: 'Copied to Clipboard',
        description: 'Output content has been copied.',
        className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
      });
    } catch (err) {
      toast({
        title: 'Copy Failed',
        description: 'Unable to copy content to clipboard.',
        variant: 'destructive'
      });
    }
  };

  // Get agent category and style profile
  const agentCategory = useMemo(() => {
    return detectAgentCategory(session?.agent_name, { category: session?.agent_category });
  }, [session]);
  
  const styleProfile = useMemo(() => {
    return getOutputStyleProfile(agentCategory);
  }, [agentCategory]);

  // Export using professional templates
  const handleExport = async (format = 'markdown') => {
    try {
      const sessionWithOutput = { ...session, output: outputData };
      
      if (format === 'markdown') {
        exportToMarkdown(sessionWithOutput, styleProfile);
      } else if (format === 'pdf') {
        await exportToPDF(sessionWithOutput, styleProfile, activeOrganization);
      } else if (format === 'docx') {
        await exportToDOCX(sessionWithOutput, styleProfile, activeOrganization);
      }
      
      toast({
        title: 'Export Complete',
        description: `Report exported as ${format.toUpperCase()}`,
        className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
      });
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: 'Export Failed',
        description: 'Unable to export content.',
        variant: 'destructive'
      });
    }
  };

  const handleMarkReviewed = () => {
    setIsReviewed(!isReviewed);
    toast({
      title: isReviewed ? 'Marked as Pending' : 'Marked as Reviewed',
      description: isReviewed ? 'This analysis is pending review.' : 'This analysis has been reviewed.',
      className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
    });
  };

  // Determine effective status considering both session.status and outputData.execution_status
  const getEffectiveStatus = () => {
    if (outputData?.execution_status === 'error') {
      return 'failed';
    }
    if (outputData?.execution_status === 'fallback') {
      return 'fallback';
    }
    return session?.status || 'created';
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'Completed' };
      case 'running':
        return { icon: CircleNotch, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'Running', spin: true };
      case 'failed':
        return { icon: WarningCircle, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'Failed' };
      case 'fallback':
        return { icon: WarningCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'Partial' };
      case 'queued':
      case 'created':
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: 'Queued' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: status };
    }
  };

  // Extract executive summary and recommendations from sections or markdown
  const { executiveSummary, recommendations, otherSections } = useMemo(() => {
    const content = outputData?.content || {};
    const sections = content.sections || [];
    const markdown = content.markdown || '';
    
    let summary = null;
    let recs = [];
    let others = [];
    
    // Check sections for Summary and Recommendations
    sections.forEach(section => {
      const key = (section.key || section.label || '').toLowerCase();
      if (key.includes('summary') || key.includes('executive') || key.includes('overview')) {
        summary = section;
      } else if (key.includes('recommendation') || key.includes('next step') || key.includes('action')) {
        recs.push(section);
      } else {
        others.push(section);
      }
    });
    
    // If no structured sections, try to parse from markdown
    if (!summary && markdown) {
      // Look for summary section in markdown
      const summaryMatch = markdown.match(/##?\s*(Executive\s+)?Summary[:\s]*([\s\S]*?)(?=##|$)/i);
      if (summaryMatch) {
        summary = { key: 'summary', label: 'Executive Summary', content: summaryMatch[2].trim(), type: 'markdown' };
      }
      
      // Look for recommendations in markdown
      const recsMatch = markdown.match(/##?\s*(Recommendations?|Next\s+Steps?|Actions?)[:\s]*([\s\S]*?)(?=##|$)/i);
      if (recsMatch) {
        recs.push({ key: 'recommendations', label: 'Recommendations', content: recsMatch[2].trim(), type: 'markdown' });
      }
    }
    
    return { 
      executiveSummary: summary, 
      recommendations: recs, 
      otherSections: others 
    };
  }, [outputData]);

  // Parse action items from recommendations
  const actionItems = useMemo(() => {
    const items = [];
    const actionPayload = outputData?.action_payload;
    
    // Check action_payload first
    if (actionPayload?.actions) {
      actionPayload.actions.forEach(action => {
        items.push({
          icon: Lightning,
          text: action.description || action.label || action.action,
          type: action.type || 'action'
        });
      });
    }
    
    // Parse from recommendations sections
    recommendations.forEach(rec => {
      const content = rec.content || '';
      // Parse bullet points or numbered items
      const bulletItems = content.match(/^[\s]*[-•*\d.]+[\s]+(.+)$/gm) || [];
      bulletItems.forEach(item => {
        const cleanText = item.replace(/^[\s]*[-•*\d.]+[\s]+/, '').trim();
        if (cleanText && cleanText.length > 10) {
          items.push({
            icon: items.length < 3 ? Target : Lightbulb,
            text: cleanText,
            type: 'recommendation'
          });
        }
      });
    });
    
    return items.slice(0, 6); // Max 6 action items
  }, [recommendations, outputData]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  // Render Executive Summary with highlight
  const renderExecutiveSummary = () => {
    if (!executiveSummary) return null;
    
    return (
      <div className="mb-6 p-5 bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-[#6366F1]/20 rounded-lg">
            <Target size={16} className="text-[#6366F1]" />
          </div>
          <h3 className="text-sm font-semibold text-[#6366F1] uppercase tracking-wide">Executive Summary</h3>
        </div>
        <div className="prose prose-invert prose-sm max-w-none text-gray-200">
          {executiveSummary.type === 'markdown' ? (
            <ReactMarkdown>{executiveSummary.content}</ReactMarkdown>
          ) : (
            <p className="whitespace-pre-wrap leading-relaxed">{executiveSummary.content}</p>
          )}
        </div>
      </div>
    );
  };

  // Render structured section card
  const renderSectionCard = (section, idx) => {
    const isExpanded = expandedSections[section.key] !== false;
    const sectionKey = section.key || `section-${idx}`;
    
    // Section icon based on label/key
    const getSectionIcon = (label) => {
      const lower = (label || '').toLowerCase();
      if (lower.includes('analysis')) return MagnifyingGlass;
      if (lower.includes('risk')) return WarningCircle;
      if (lower.includes('cost') || lower.includes('financial')) return Database;
      if (lower.includes('time')) return Clock;
      return FileText;
    };
    
    const SectionIcon = getSectionIcon(section.label || section.key);
    
    return (
      <div 
        key={sectionKey} 
        className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden transition-all duration-200 hover:border-[rgba(255,255,255,0.15)]"
      >
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full p-4 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-[rgba(255,255,255,0.05)] rounded-lg">
              <SectionIcon size={14} className="text-gray-400" />
            </div>
            <span className="text-sm font-medium text-white">{section.label || section.key}</span>
          </div>
          {isExpanded ? (
            <CaretUp size={16} className="text-gray-500" />
          ) : (
            <CaretDown size={16} className="text-gray-500" />
          )}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-[rgba(255,255,255,0.05)]">
            <div className="pt-4 text-sm text-gray-300 leading-relaxed">
              {section.type === 'code' ? (
                <pre className="bg-[rgba(0,0,0,0.4)] p-4 rounded-lg overflow-x-auto font-mono text-xs">
                  <code>{section.content}</code>
                </pre>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{section.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Suggested Actions Panel
  const renderSuggestedActions = () => {
    if (actionItems.length === 0) return null;
    
    return (
      <div className="mt-6 p-5 bg-[#0D0D0D] border border-[rgba(255,255,255,0.08)] rounded-xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-[#14F195]/10 rounded-lg">
            <Lightning size={16} className="text-[#14F195]" />
          </div>
          <h3 className="text-sm font-semibold text-white">Suggested Actions</h3>
        </div>
        
        <div className="space-y-3">
          {actionItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div 
                key={item.id || item.text || `action-${idx}`}
                className="flex items-start gap-3 p-3 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] rounded-lg group hover:border-[#6366F1]/30 transition-colors"
              >
                <div className="p-1 bg-[rgba(255,255,255,0.05)] rounded mt-0.5">
                  <Icon size={12} className="text-gray-400 group-hover:text-[#6366F1]" />
                </div>
                <p className="text-sm text-gray-300 flex-1 leading-relaxed">{item.text}</p>
              </div>
            );
          })}
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[rgba(255,255,255,0.05)]">
          <button 
            className="px-3 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-1.5"
            onClick={() => handleExport('markdown')}
          >
            <DownloadSimple size={12} />
            Export
          </button>
        </div>
      </div>
    );
  };

  // Render output actions bar
  const renderOutputActions = () => {
    return (
      <div className="flex items-center gap-2 mb-4">
        {/* PaperPlaneRight to Tray - Only visible for member users, not admins */}
        {showSendToInbox && (
          <button
            onClick={handleSendToInbox}
            disabled={inboxItemExists || sendingToInbox}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
              inboxItemExists 
                ? 'bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/30 cursor-default' 
                : 'bg-[rgba(255,255,255,0.05)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.1)]'
            } disabled:opacity-50`}
            title={inboxItemExists ? 'Already in Inbox' : 'Send to Inbox'}
          >
            {sendingToInbox ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <Tray size={14} />
            )}
            {inboxItemExists ? 'In Inbox' : 'Send to Inbox'}
          </button>
        )}
        
        {showSendToInbox && <div className="w-px h-5 bg-[rgba(255,255,255,0.1)] mx-1" />}
        
        <button
          onClick={handleCopyOutput}
          className="p-2 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-colors"
          title="Copy to Clipboard"
        >
          <Copy size={16} />
        </button>
        
        {/* Universal Export Button - Same for ALL agents */}
        <button
          onClick={() => setShowExportModal(true)}
          className="px-3 py-1.5 text-sm bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg transition-colors flex items-center gap-1.5"
          title="Export Results"
        >
          <ShareNetwork size={14} />
          Export
        </button>
        
        <button
          onClick={handleRerun}
          disabled={rerunning}
          className="p-2 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-colors disabled:opacity-50"
          title="Run Again"
        >
          <ArrowsClockwise size={16} className={rerunning ? 'animate-spin' : ''} />
        </button>
        <button
          onClick={handleMarkReviewed}
          className={`p-2 rounded-lg transition-colors ${isReviewed ? 'text-[#14F195] bg-[#14F195]/10' : 'text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)]'}`}
          title={isReviewed ? 'Marked as Reviewed' : 'Mark as Reviewed'}
        >
          <CheckSquare size={16} />
        </button>
      </div>
    );
  };

  // Render markdown content
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    
    return (
      <div className="p-5 bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl">
        <div className="prose prose-invert prose-sm max-w-none text-gray-300 
          prose-headings:text-white prose-headings:font-semibold
          prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-3
          prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2
          prose-p:leading-relaxed prose-p:my-3
          prose-li:my-1
          prose-strong:text-white prose-strong:font-semibold
          prose-code:bg-[rgba(255,255,255,0.1)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[#6366F1]">
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </div>
      </div>
    );
  };

  // Render JSON content
  const renderJson = (jsonData) => {
    if (!jsonData || Object.keys(jsonData).length === 0) return null;
    
    return (
      <div className="p-5 bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl">
        <pre className="text-sm text-gray-300 whitespace-pre-wrap overflow-x-auto font-mono">
          {JSON.stringify(jsonData, null, 2)}
        </pre>
      </div>
    );
  };

  // Render side effects section
  const renderSideEffects = (sideEffects) => {
    if (!sideEffects?.proposed || sideEffects.proposed.length === 0) return null;
    
    const statusColors = {
      proposed: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      applied: 'bg-green-500/20 text-green-300 border-green-500/30',
      skipped: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      failed: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    
    return (
      <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
        <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Database size={18} />
          Data Changes
        </h2>
        <div className="space-y-3">
          {sideEffects.proposed.map((effect, idx) => (
            <div key={effect.id || `${effect.target || ''}-${effect.action || ''}-${idx}`} className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white capitalize">{effect.target}</span>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-sm text-gray-300 capitalize">{effect.action}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border ${statusColors[effect.status] || statusColors.proposed}`}>
                  {effect.status}
                </span>
              </div>
              {effect.message && (
                <p className="text-xs text-gray-400">{effect.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render artifacts section
  const renderArtifacts = (artifacts) => {
    if (!artifacts || artifacts.length === 0) return null;
    
    return (
      <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
        <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <FileAudio size={18} />
          Artifacts
        </h2>
        <div className="space-y-3">
          {artifacts.map((artifact, idx) => (
            <div key={idx} className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                {artifact.type === 'audio' ? (
                  <SpeakerHigh size={16} className="text-purple-400" />
                ) : artifact.type === 'transcript' ? (
                  <FileText size={16} className="text-blue-400" />
                ) : (
                  <FileText size={16} className="text-gray-400" />
                )}
                <span className="text-sm font-medium text-white">{artifact.label}</span>
                <span className="text-xs text-gray-500 capitalize">({artifact.type})</span>
              </div>
              
              {artifact.type === 'transcript' && artifact.content && (
                <div className="mt-2 p-3 bg-[rgba(0,0,0,0.2)] rounded text-sm text-gray-300 whitespace-pre-wrap max-h-60 overflow-y-auto">
                  {artifact.content}
                </div>
              )}
              
              {artifact.type === 'audio' && artifact.storage_url && (
                <audio controls className="w-full mt-2">
                  <source src={artifact.storage_url} type={artifact.mime_type || 'audio/wav'} />
                  Your browser does not support the audio element.
                </audio>
              )}
              
              {artifact.storage_url && (
                <a 
                  href={artifact.storage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#6366F1] hover:text-[#8B5CF6] mt-2"
                >
                  <ArrowSquareOut size={12} />
                  Open
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render runtime metadata
  const renderRuntimeMetadata = (runtime) => {
    if (!runtime) return null;
    
    return (
      <div className="p-3 bg-[rgba(99,102,241,0.05)] border border-[rgba(99,102,241,0.2)] rounded-lg text-xs text-gray-400 mt-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {runtime.llm_provider && (
            <div><span className="text-gray-500">Provider:</span> {runtime.llm_provider}</div>
          )}
          {runtime.llm_model && (
            <div><span className="text-gray-500">Model:</span> {runtime.llm_model}</div>
          )}
          {runtime.key_source && (
            <div><span className="text-gray-500">Key:</span> {runtime.key_source}</div>
          )}
          {runtime.tokens_in > 0 && (
            <div><span className="text-gray-500">Tokens In:</span> {runtime.tokens_in}</div>
          )}
          {runtime.tokens_out > 0 && (
            <div><span className="text-gray-500">Tokens Out:</span> {runtime.tokens_out}</div>
          )}
          {runtime.latency_ms > 0 && (
            <div><span className="text-gray-500">Latency:</span> {(runtime.latency_ms / 1000).toFixed(2)}s</div>
          )}
        </div>
      </div>
    );
  };

  // Render error block for failed executions
  const renderErrorBlock = (error) => {
    if (!error) return null;
    
    return (
      <div className="p-5 bg-red-500/10 border border-red-500/30 rounded-xl">
        <div className="flex items-center gap-2 mb-3">
          <WarningCircle size={18} className="text-red-400" />
          <h3 className="text-sm font-semibold text-red-400">Execution Failed</h3>
        </div>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-red-300 font-medium">Error Type:</span>
            <span className="ml-2 text-white font-mono text-xs">{error.error_type}</span>
          </div>
          <div>
            <span className="text-red-300 font-medium">Details:</span>
            <p className="mt-1 text-gray-300">{error.error_message}</p>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Please check your API key configuration or contact support.
        </p>
      </div>
    );
  };

  // Detect renderer type based on output structure
  const rendererType = useMemo(() => {
    return detectRendererType(outputData);
  }, [outputData]);
  
  // Get label for the renderer type
  const outputLabel = useMemo(() => {
    return getRendererLabel(rendererType);
  }, [rendererType]);
  
  // Handle actions from renderers
  const handleRendererAction = (action, param) => {
    switch (action) {
      case 'create_automation':
        toast({
          title: 'Coming Soon',
          description: 'Automation creation will be available soon.',
          className: 'bg-[#1a1a1a] border-[#6366F1]/30 text-white'
        });
        break;
      case 'export':
        if (param === 'pdf') handleExport('pdf');
        else if (param === 'docx') handleExport('docx');
        else if (param === 'csv') handleExport('markdown'); // CSV fallback
        else handleExport('markdown');
        break;
      case 'send_to_inbox':
        handleSendToInbox();
        break;
      case 'copied':
        toast({
          title: 'Copied',
          description: 'Content copied to clipboard.',
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        break;
      case 'assign':
        toast({
          title: 'Coming Soon',
          description: 'Task assignment will be available soon.',
          className: 'bg-[#1a1a1a] border-[rgba(255,255,255,0.2)] text-white'
        });
        break;
      default:
        console.log('Renderer action:', action, param);
    }
  };

  // Main output renderer - Output-Type-Aware Rendering
  // Core Principle: Agents do not choose layouts. Output structure determines rendering.
  const renderOutput = () => {
    // Check for UOS v2.0 output format first (new Universal Output Framework)
    const uosOutput = session?.uos_output || outputData?.uos_output;
    if (uosOutput && uosOutput.version === '2.0') {
      return (
        <UniversalOutputViewer 
          output={uosOutput} 
          onExport={(output) => setShowExportModal(true)}
        />
      );
    }
    
    if (outputData) {
      const executionStatus = outputData.execution_status || 'success';
      const outputError = outputData.error;
      const content = outputData.content || {};
      const markdown = content.markdown;
      
      // If execution failed, show error block prominently
      if (executionStatus === 'error' && outputError) {
        return (
          <div className="space-y-4">
            {renderErrorBlock(outputError)}
            {markdown && <DocumentRenderer content={content} outputData={outputData} onAction={handleRendererAction} />}
          </div>
        );
      }
      
      // For fallback status, show warning then render
      if (executionStatus === 'fallback') {
        return (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <WarningCircle size={16} className="text-yellow-400" />
                <span className="text-sm font-medium text-yellow-400">Partial Result</span>
              </div>
              <p className="text-sm text-gray-300">The agent completed but returned limited output.</p>
            </div>
            {/* Still render what we have using appropriate renderer */}
            {renderOutputByType()}
          </div>
        );
      }
      
      // Success state - render with output-type-aware renderer
      return renderOutputByType();
    }
    
    // Fallback for no outputData
    return <PlainTextRenderer content={null} outputData={session?.output} onAction={handleRendererAction} />;
  };
  
  // Render output based on detected type
  const renderOutputByType = () => {
    const content = outputData?.content || {};
    
    // For insight renderer, ensure sections are populated (parse from markdown if needed)
    const getContentWithSections = () => {
      const sections = content.sections || [];
      
      // Check if sections are meaningful (more than 1, or not just a single "output"/"analysis" bucket)
      const hasMeaningfulSections = sections.length > 1 || 
        (sections.length === 1 && 
         !['output', 'analysis'].includes(sections[0]?.key?.toLowerCase()) &&
         !sections[0]?.label?.toLowerCase().includes('analysis'));
      
      if (hasMeaningfulSections) {
        return content;
      }
      
      // Re-parse sections from markdown for better rendering
      const markdown = content.markdown || (sections.length === 1 ? sections[0]?.content : '');
      if (markdown) {
        const parsedSections = parseMarkdownToSections(markdown);
        if (parsedSections.length >= 2) {
          return { ...content, sections: parsedSections };
        }
      }
      
      return content;
    };
    
    switch (rendererType) {
      case 'insight':
        return <InsightRenderer content={getContentWithSections()} onAction={handleRendererAction} />;
      
      case 'action':
        return <ActionRenderer content={content} outputData={outputData} onAction={handleRendererAction} />;
      
      case 'data':
        return <DataRenderer content={content} outputData={outputData} onAction={handleRendererAction} />;
      
      case 'document':
        return <DocumentRenderer content={content} outputData={outputData} onAction={handleRendererAction} />;
      
      case 'plaintext':
      default:
        return <PlainTextRenderer content={content} outputData={outputData} onAction={handleRendererAction} />;
    }
  };
  
  // Legacy output rendering (backwards compatibility for session.output)
  const renderLegacyOutput = () => {
    if (!session?.output) return null;
    
    const output = session.output;
    
    // Teams Meeting Output
    if (output.type === 'teams_meeting') {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-400 mb-3">Microsoft Teams Meeting Data</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-400">Meeting ID:</span>
                <span className="ml-2 text-white font-mono text-xs">{output.meeting_id}</span>
              </div>
              <div>
                <span className="text-gray-400">Transcripts Found:</span>
                <span className="ml-2 text-white">{output.transcripts_count}</span>
              </div>
              <div>
                <span className="text-gray-400">Recordings Found:</span>
                <span className="ml-2 text-white">{output.recordings_count}</span>
              </div>
            </div>
          </div>
          
          {output.transcript_preview && (
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Transcript Preview</h3>
              <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                <p className="text-sm text-gray-300 whitespace-pre-wrap">
                  {output.transcript_preview}
                  {output.transcript_preview.length >= 500 && '...'}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Text Response Output
    if (output.type === 'text_response') {
      return (
        <div>
          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg mb-4">
            <p className="text-sm text-white">{output.response}</p>
          </div>
          {output.metadata && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-300">
              <div>Input Length: {output.metadata.input_length} characters</div>
              <div>Execution Mode: {output.metadata.execution_mode}</div>
            </div>
          )}
        </div>
      );
    }
    
    // Audio Analysis Output - render the analysis markdown properly
    if (output.type === 'audio_analysis' && output.analysis) {
      // Parse sections from the analysis markdown for proper rendering
      const sections = parseMarkdownToSections(output.analysis);
      if (sections.length >= 2) {
        return <InsightRenderer content={{ sections, markdown: output.analysis }} onAction={handleRendererAction} />;
      }
      // Fallback to document renderer for simpler markdown
      return <DocumentRenderer content={{ markdown: output.analysis }} outputData={output} onAction={handleRendererAction} />;
    }
    
    // File Analysis Output - same treatment as audio analysis
    if (output.type === 'file_analysis' && output.response) {
      const sections = parseMarkdownToSections(output.response);
      if (sections.length >= 2) {
        return <InsightRenderer content={{ sections, markdown: output.response }} onAction={handleRendererAction} />;
      }
      return <DocumentRenderer content={{ markdown: output.response }} outputData={output} onAction={handleRendererAction} />;
    }
    
    // Generic text output with 'response' field
    if (output.response && typeof output.response === 'string') {
      const sections = parseMarkdownToSections(output.response);
      if (sections.length >= 2) {
        return <InsightRenderer content={{ sections, markdown: output.response }} onAction={handleRendererAction} />;
      }
      return <DocumentRenderer content={{ markdown: output.response }} outputData={output} onAction={handleRendererAction} />;
    }
    
    // Generic text output with 'analysis' field (backup)
    if (output.analysis && typeof output.analysis === 'string') {
      const sections = parseMarkdownToSections(output.analysis);
      if (sections.length >= 2) {
        return <InsightRenderer content={{ sections, markdown: output.analysis }} onAction={handleRendererAction} />;
      }
      return <DocumentRenderer content={{ markdown: output.analysis }} outputData={output} onAction={handleRendererAction} />;
    }
    
    // Voice Response Output
    if (output.type === 'voice_response') {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Microphone size={16} className="text-purple-400" />
              <h3 className="text-sm font-semibold text-purple-400">Voice Session Output</h3>
            </div>
            {output.transcript && (
              <div className="mb-3">
                <p className="text-xs text-gray-400 mb-1">Transcript:</p>
                <p className="text-sm text-white bg-[rgba(0,0,0,0.2)] p-3 rounded">{output.transcript}</p>
              </div>
            )}
            <p className="text-sm text-gray-300">{output.response}</p>
          </div>
        </div>
      );
    }
    
    // Generic/Fallback - use PlainTextRenderer
    return <PlainTextRenderer content={output} outputData={output} onAction={handleRendererAction} />;
  };

  // Render voice session info
  const renderVoiceSessionInfo = () => {
    const input = session?.input;
    const inputPayload = session?.input_payload;
    const isVoice = input?.mode === 'voice' || session?.input_type === 'voice';
    
    if (!isVoice) return null;
    
    const voiceData = input?.voice || {};
    const transcript = voiceData.transcript || inputPayload?.transcript;
    const audioAssetId = voiceData.audio_asset_id;
    const runtime = session?.runtime || {};
    
    return (
      <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] mb-6">
        <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Microphone size={18} className="text-purple-400" />
          Voice Session
        </h2>
        
        <div className="space-y-4">
          {transcript && (
            <div>
              <p className="text-sm text-gray-400 mb-2">Transcript</p>
              <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white whitespace-pre-wrap">
                {transcript}
              </div>
            </div>
          )}
          
          {audioAssetId && (
            <div>
              <p className="text-sm text-gray-400 mb-2">Audio Recording</p>
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg flex items-center gap-2">
                <SpeakerHigh size={16} className="text-purple-400" />
                <span className="text-sm text-gray-300">Audio asset: {audioAssetId}</span>
              </div>
            </div>
          )}
          
          {(runtime.voice_provider || runtime.stt_provider || runtime.tts_provider) && (
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg text-xs text-purple-300">
              <p className="font-medium mb-1">Voice Providers</p>
              <div className="grid grid-cols-2 gap-2">
                {runtime.voice_provider && <div>Provider: {runtime.voice_provider}</div>}
                {runtime.stt_provider && <div>STT: {runtime.stt_provider}</div>}
                {runtime.tts_provider && <div>TTS: {runtime.tts_provider}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING & ERROR STATES
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-5xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-48 mb-8 animate-pulse"></div>
          <div className="space-y-4">
            <div className="glass rounded-xl p-6 h-40 animate-pulse"></div>
            <div className="glass rounded-xl p-6 h-60 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <WarningCircle size={64} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">Session Not Found</h2>
          <button
            onClick={() => navigate('/partner-pro/workspace/sessions')}
            className="px-6 py-3 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg transition-colors"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  const effectiveStatus = getEffectiveStatus();
  const statusInfo = getStatusInfo(effectiveStatus);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/partner-pro/workspace/sessions')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Sessions
        </button>

        {/* Status Header - Work-oriented language */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-2">{session.agent_name}</h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className={`flex items-center gap-2 text-sm px-3 py-1 ${statusInfo.bg} ${statusInfo.color} border ${statusInfo.border} rounded`}>
                  <StatusIcon size={16} className={statusInfo.spin ? 'animate-spin' : ''} />
                  {statusInfo.text}
                </div>
                
                {/* Agent Category Badge */}
                <div 
                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded"
                  style={{ 
                    backgroundColor: `${styleProfile.colorAccent}15`,
                    color: styleProfile.colorAccent,
                    border: `1px solid ${styleProfile.colorAccent}30`
                  }}
                >
                  {styleProfile.name} Agent
                </div>
                
                <span className="text-sm text-gray-400">
                  {(() => {
                    const ts = session.completed_at || session.created_at;
                    // Ensure UTC timestamp is parsed correctly
                    const dateStr = typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+') ? ts + 'Z' : ts;
                    return new Date(dateStr).toLocaleString();
                  })()}
                </span>
                {isReviewed && (
                  <span className="flex items-center gap-1 text-xs text-[#14F195] bg-[#14F195]/10 px-2 py-1 rounded">
                    <CheckSquare size={12} />
                    Reviewed
                  </span>
                )}
              </div>
              
              {/* Status reinforcement message */}
              {effectiveStatus === 'completed' && (
                <p className="text-sm text-gray-400 mt-3">
                  This analysis is ready for review and action.
                </p>
              )}
            </div>

            {(session.status === 'completed' || session.status === 'failed') && (
              <button
                onClick={handleRerun}
                disabled={rerunning}
                className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {rerunning ? (
                  <>
                    <CircleNotch size={16} className="animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Run Again
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Voice Session Info (if applicable) */}
        {renderVoiceSessionInfo()}

        {/* Inputs - Collapsible */}
        <details className="glass rounded-xl border border-[rgba(255,255,255,0.1)] mb-6 group">
          <summary className="p-4 cursor-pointer flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)]">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <FileText size={18} />
              Input Parameters
            </h2>
            <CaretDown size={16} className="text-gray-400 group-open:rotate-180 transition-transform" />
          </summary>
          <div className="px-6 pb-6 border-t border-[rgba(255,255,255,0.05)]">
            <div className="space-y-3 pt-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Input Type</p>
                <span className="text-sm px-2 py-1 bg-[rgba(255,255,255,0.05)] text-white rounded">
                  {session.input?.mode || session.input_type}
                </span>
              </div>

              {session.input_payload?.text && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Instructions</p>
                  <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg text-sm text-white whitespace-pre-wrap">
                    {session.input_payload.text}
                  </div>
                </div>
              )}

              {session.input_payload?.meeting_link && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">Meeting Link</p>
                  <a
                    href={session.input_payload.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#6366F1] hover:text-[#8B5CF6]"
                  >
                    <LinkIcon size={14} />
                    {session.input_payload.meeting_link}
                  </a>
                </div>
              )}

              {session.input_payload?.meeting_url && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">Teams Meeting URL</p>
                  <a
                    href={session.input_payload.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#6366F1] hover:text-[#8B5CF6]"
                  >
                    <LinkIcon size={14} />
                    {session.input_payload.meeting_url}
                  </a>
                </div>
              )}
            </div>
          </div>
        </details>

        {/* Output Section - Output-type determines rendering */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{outputLabel}</h2>
            {session.status === 'completed' && !outputLoading && renderOutputActions()}
          </div>
          
          {outputLoading && (
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
              <CircleNotch size={20} className="text-blue-400 animate-spin" />
              <p className="text-sm text-blue-300">Loading analysis...</p>
            </div>
          )}
          
          {!outputLoading && session.status === 'completed' && (
            <>
              {/* AI Output Notice - EU AI Act Aligned Transparency (Per-Agent) */}
              <AIOutputNotice 
                variant="inline" 
                noticeType={session.ai_output_notice_type || outputData?.ai_output_notice_type || 'general'} 
                className="mb-4" 
              />
              
              {/* Use new output-type-aware rendering if outputData exists */}
              {outputData ? renderOutput() : renderLegacyOutput()}
              
              {/* Runtime Metadata */}
              {session.runtime && renderRuntimeMetadata(session.runtime)}
            </>
          )}

          {session.status === 'failed' && (session.error || session.errors?.message) && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm font-medium text-red-400 mb-2">Error</p>
              <p className="text-sm text-red-300">{session.errors?.message || session.error}</p>
            </div>
          )}

          {session.status === 'running' && (
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
              <CircleNotch size={20} className="text-blue-400 animate-spin" />
              <p className="text-sm text-blue-300">Agent is processing your request...</p>
            </div>
          )}

          {(session.status === 'queued' || session.status === 'created') && (
            <div className="p-4 bg-gray-500/10 border border-gray-500/30 rounded-lg flex items-center gap-3">
              <Clock size={20} className="text-gray-400" />
              <p className="text-sm text-gray-300">Session queued for processing</p>
            </div>
          )}
        </div>

        {/* Side Effects (if available) */}
        {outputData && renderSideEffects(outputData.side_effects)}

        {/* Artifacts (if available) */}
        {outputData && renderArtifacts(outputData.artifacts)}
      </div>
      
      {/* Universal Export Modal - Available for ALL agents */}
      <UniversalExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        agentId={session?.agent_id}
        agentName={session?.agent_name}
        sessionId={session?.id || session?.session_id}
        contextTitle={session?.input_payload?.title || session?.input_payload?.meeting_title}
        sourceFilename={session?.input_payload?.filename}
        outputData={outputData}
        exportPreviewSchema={session?.export_preview_schema}
        exportPreviewEnabled={session?.export_preview_enabled}
        uosOutput={session?.uos_output || outputData?.uos_output}
      />
    </div>
  );
};

export default SessionDetail;
